import { useTranslation } from "react-i18next";
import { InfoBox } from "../../../components/InfoBox";
import {
  SWISH_TECHNICAL_SUPPLIER_NAME,
  SWISH_TECHNICAL_SUPPLIER_ORG_NUMBER,
} from "../../../constants/swish";
import { FormSection } from "../../../components/FormSection";
import { getEffectiveBookkeepingMapping } from "@KassaroAB/shared/utils/bookkeepingDefaultAccounts";
import type { PaymentMethodSwishCommerce } from "@KassaroAB/shared/types/paymentMethods/paymentMethod";
import { FieldGroupPaymentMethodFields } from "../paymentMethodFieldGroup";
import { usePaymentMethodForm } from "../paymentMethodFormHook";
import {
  PaymentMethodFormContainer,
  type PaymentMethodFormProps,
} from "./PaymentMethodFormContainer";
import { basePaymentMethodValues } from "./basePaymentMethodValues";
import { Fields, TextField } from "./paymentMethodFields";

export const SwishCommerceForm = ({
  formId,
  store,
  paymentMethod,
  savedTick,
  onSubmit,
  onDirtyChange,
}: PaymentMethodFormProps<Omit<PaymentMethodSwishCommerce, "id">>) => {
  const { t } = useTranslation();
  const mergedAccounts = getEffectiveBookkeepingMapping(
    store.country,
    store.bookkeepingDefaultAccounts,
  );
  const form = usePaymentMethodForm({
    defaultValues: {
      ...basePaymentMethodValues(paymentMethod),
      swishNumber: paymentMethod.swishNumber,
      messageToCustomer: paymentMethod.messageToCustomer ?? "",
    },
    onSubmit: ({ value }) => onSubmit(value),
  });

  return (
    <PaymentMethodFormContainer
      formId={formId}
      form={form}
      savedTick={savedTick}
      onDirtyChange={onDirtyChange}
    >
      <FieldGroupPaymentMethodFields
        form={form}
        fields={{
          name: "name",
          type: "type",
          bookkeepingAccountId: "bookkeepingAccountId",
          color: "color",
        }}
        defaultAccountNumber={mergedAccounts.businessAccount}
        t={t}
      />
      <FormSection title={t("paymentMethodForm.swish.additionalSettings")}>
        <InfoBox variant="info">
          {t("paymentMethodForm.swish.technicalSupplierInfo", {
            companyName: SWISH_TECHNICAL_SUPPLIER_NAME,
            orgNumber: SWISH_TECHNICAL_SUPPLIER_ORG_NUMBER,
          })}
        </InfoBox>
        <Fields>
          <form.Field name="swishNumber">
            {(field) => (
              <TextField
                label={t("paymentMethodForm.swish.swishNumber")}
                value={field.state.value}
                onChange={field.handleChange}
                onBlur={field.handleBlur}
              />
            )}
          </form.Field>
          <form.Field name="messageToCustomer">
            {(field) => (
              <TextField
                label={t("paymentMethodForm.swish.messageToCustomer")}
                value={field.state.value}
                onChange={field.handleChange}
                onBlur={field.handleBlur}
              />
            )}
          </form.Field>
        </Fields>
      </FormSection>
    </PaymentMethodFormContainer>
  );
};
